import React, { useEffect, useState } from "react";
import styles from './CreateInstructor.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import InstructorForm from "../../../components/instructorForm/InstructorForm";
import axiosClient from "../../../api/axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import { formatPhoneNumber } from "../../../utils/stringUtils";

const CreateInstructor = () => {
  const [isLoading, setIsLoading] = useState(false);
  const initialFormData = {
    profilePicture: null,
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialityId: null,
    qualification: '',
    healthSystemIds: [],
    clinicalSiteIds: [],
    roleId: '',
    isActive: true
  };
  const [formData, setFormData] = useState(initialFormData);
  const [titleList, setTitleList] = useState([]);
  const [specialityList, setSpecialityList] = useState([]);
  const [healthSystemList, setHealthSystemsList] = useState([]);
  const [clinicalSiteList, setClinicalSiteList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [isTemporaryInstructor, setIsTemporaryInstructor] = useState(false);
  const { instructorId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      axiosClient.get('specialities')
      .then ((specialityResponse) => {
        setSpecialityList(specialityResponse.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      })
    }

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`health-systems`)
      .then((response) => {
        setHealthSystemsList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`clinical-sites`, {
        params: {
          healthSystemIds: formData.healthSystemIds.join(',')
        }
      })
      .then((response) => {
        setClinicalSiteList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
        setFormData((prevData) => {
          const existingClinicalSiteIds = prevData.clinicalSiteIds;
          const availableClinicalSiteIds = response.data.map(item => item.id);
          const filteredClinicalSiteIds = 
            existingClinicalSiteIds.filter(id => availableClinicalSiteIds.includes(id));
          return {
            ...prevData,
            clinicalSiteIds: filteredClinicalSiteIds
          } 
        });
      })
    };

    if(formData.healthSystemIds.length > 0) {
      fetchData();
    } else {
      setClinicalSiteList([]);
      setFormData((prevData) => {
        return {
          ...prevData,
          clinicalSiteIds: []
        }
      });
    }
  }, [formData.healthSystemIds]);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`/instructors/options`)
      .then((response) => {
        if(response.data?.titles) {
          setTitleList(response.data?.titles.map((item) => ({
            value: item,
            label: item
          })));
        }
        if (response.data?.designations) {
          const newRoleList = response.data?.designations.map((item) => ({
            value: item.id,
            label: item.name
          }));
        
          setRoleList(newRoleList);
        
          setFormData((prevData) => {
            if (!prevData.roleId && newRoleList.length > 0) {
              return {
                ...prevData,
                roleId: newRoleList[0].value
              };
            }
            return prevData;
          });
        }
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`users/instructor/${instructorId}`)
      .then((response) => {
        setFormData((prevData) => {
          return {
            ...prevData,
            profilePicture: response?.data?.profilePicture,
            title: response?.data?.title,
            firstName: response?.data?.firstName,
            lastName: response?.data?.lastName,
            email: response?.data?.email,
            phone: response?.data?.phone !== '0' ? formatPhoneNumber(response?.data?.phone) : '',
            specialityId: response.data?.speciality ? response.data?.speciality.id : null,
            qualification: response?.data?.qualification,
            healthSystemIds: response.data?.healthSystems ? response.data?.healthSystems.map(item => item.id) : [],
            clinicalSiteIds: response.data?.clinicalSites ? response.data?.clinicalSites.map(item => item.id) : [],
            isActive: response?.data?.isActive ?? true,
          }
        });
        if(response.data?.currentRole?.role === 'Temporary Instructor') {
          setIsTemporaryInstructor(true);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    }

    if(instructorId) {
      fetchData()
    }
  }, [instructorId, showToast]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSave = async (imageFile) => {
    const data = {
      title: formData.title,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobileNumber: formData.phone.replace(/\D/g, ''),
      specialityId: formData.specialityId ?? null,
      qualification: formData.qualification,
      healthSystemIds: formData.healthSystemIds,
      clinicalSiteIds: formData.clinicalSiteIds,
      roleId: formData.roleId,
      status: formData.isActive
    }
    const updatedFormData = new FormData();
    for (let key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (Array.isArray(data[key])) {
          data[key].forEach(item => {
            if (item != null) { // Check for null or undefined
              updatedFormData.append(`${key}[]`, item);
            }
          });
        } else {
          if (data[key] != null) { // Check for null or undefined
            updatedFormData.append(key, data[key]);
          }
        }
      }
    }
    if(imageFile) {
      updatedFormData.append('profilePicture', imageFile);
    }
    setIsLoading(true);
    if(instructorId) {
      await axiosClient.patch(`users/instructor/${instructorId}`, updatedFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      .then((response) => {
        showToast(response.message);
        navigate('/instructors');
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        scrollToTop();
      });
    } else {
      await axiosClient.post(`instructors/create`, updatedFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      .then((response) => {
        showToast(response.message);
        navigate('/instructors');
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        scrollToTop();
      });
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Instructor Management', path: '/instructors' },
            { label: instructorId ? (isTemporaryInstructor ? `Approve Instructor` : `Edit Instructor`) : `Create Instructor` }
          ]}
        />
      </div>
      <div className={styles.container}>
        <InstructorForm
          formData={formData}
          titleList={titleList}
          specialityList={specialityList}
          healthSystemList={healthSystemList}
          clinicalSiteList={clinicalSiteList}
          roleList={roleList}
          isTemporaryInstructor={isTemporaryInstructor}
          onDataChange={(updatedData) => {
            setFormData((prevData) => {
              return {
                ...prevData,
                ...updatedData
              }
            });
          }}
          onSubmit={(imageFile) => handleSave(imageFile)}
        />
      </div>
    </>
  );
};

export default CreateInstructor;